import React, { useState, useRef, useEffect } from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ROW_H } from "../constants";
import { FirstLevelListProps as BaseProps, MenuItem } from "../types";
import SvgIconUrl from "../../../helpers/SvgIcon/SvgIconUrl";
import { getBrandCustom } from "Util/getCustomColor";

const NEXTICON = require("Assets/icons/arrow-right.svg");

type Props = BaseProps & {
  onRequestClose?: () => void;
};

const FirstLevelList: React.FC<Props> = ({
  items,
  activeFirstLevel,
  setActiveFirstLevel,
  setOpenDrawerKey,
  setOpenThirdKey,
  onSelectTopIcon,
  onRequestClose,
}) => {
  const [activeSecondKey, setActiveSecondKey] = useState<string | null>(null);

  const { t } = useTranslation();
  const history = useHistory();

  const listRef = useRef<HTMLUListElement | null>(null);

  const navigateSmart = (path?: string, state?: any) => {
    if (!path) return;
    if (history.location.pathname === path) {
      history.replace(path, state);
    } else {
      history.push(path, state);
    }
  };

  const selectedPill = {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#F1F6FB"),
    color: getBrandCustom("primaryColor", "#0055A4"),
  } as const;
  const hoverPill = {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#F2F4F7"),
    color: getBrandCustom("primaryColor", "#1D2939"),
  } as const;

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    const selectedSecond = container.querySelector(
      'button.Mui-selected[data-level="second"]'
    ) as HTMLElement | null;
    const selectedFirst = container.querySelector(
      'button.Mui-selected[data-level="first"]'
    ) as HTMLElement | null;

    const toScroll =
      selectedSecond ||
      selectedFirst ||
      (container.querySelector(".Mui-selected") as HTMLElement | null);

    if (toScroll) {
      try {
        toScroll.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      } catch {
        toScroll.scrollIntoView?.({ block: "nearest", inline: "nearest" } as any);
      }
    }
  }, [activeFirstLevel, activeSecondKey]);

  const renderItem = (item: MenuItem) => {
    const hasChildren = !!item.children?.length;
    const isActive = activeFirstLevel === item.key;

    const baseSx = {
      borderRadius: 1,
      mx: 1,
      height: ROW_H,
      py: 0,
      alignItems: "center",
      fontFamily: "Manrope",
      fontWeight: 600,
      fontSize: 15,
      backgroundColor: "transparent",
      color: isActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
      "&.Mui-selected": selectedPill,
      ...(isActive ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
    } as const;

    if (item.ui === "drawer") {
      return (
        <ListItemButton
          key={item.key}
          selected={isActive}
          onClick={(e) => {
            e.stopPropagation();
            setActiveFirstLevel(item.key);
            setOpenDrawerKey(item.key);
            setOpenThirdKey(null);
            setActiveSecondKey(null);
            onSelectTopIcon?.(item.key);
          }}
          sx={baseSx}
        >
          {item.icon && (
            <ListItemIcon sx={{ minWidth: 17, mr: 1.5, color: "inherit", alignItems: "center" }}>
              <SvgIconUrl src={item.icon} size={17} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={t(item.label) ?? item.label ?? ""}
            primaryTypographyProps={{ noWrap: true, title: (t(item.label) ?? item.label ?? "") as string }}
          />
        </ListItemButton>
      );
    }

    if (item.ui === "accordion" && hasChildren) {
      const someChildSelected = item.children!.some((c) => c.key === activeSecondKey);
      const parentSelected = isActive && !someChildSelected;

      return (
        <Accordion
          key={item.key}
          disableGutters
          elevation={0}
          expanded={isActive}
          onChange={(_, open) => {
            setActiveFirstLevel(open ? item.key : null);
            setOpenDrawerKey(null);
            setOpenThirdKey(null);
            if (!open) setActiveSecondKey(null);
            onSelectTopIcon?.(open ? item.key : null);
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            mx: 0,
            my: 0,
            borderRadius: 1,
            color: isActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
            "&::before": { display: "none" },
            "&.Mui-expanded": { my: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              mx: 1,
              borderRadius: 1,
              height: ROW_H,
              "&.Mui-expanded": { height: ROW_H },
              "& .MuiAccordionSummary-content": { my: 0, py: 0 },
              "& .MuiAccordionSummary-content.Mui-expanded": { my: 0, py: 0 },
              display: "flex",
              alignItems: "center",
              ...(parentSelected ? selectedPill : {}),
              ...(parentSelected ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
              "& .MuiAccordionSummary-expandIconWrapper": {
                color: isActive ? getBrandCustom("primaryColor", "#0055A4") : "#475467",
                transition: "color .2s ease",
              },
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 17, mr: 1.5, color: "inherit", alignItems: "center" }}>
                <SvgIconUrl src={item.icon} size={17} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={t(item.label) ?? item.label ?? ""}
              primaryTypographyProps={{ noWrap: true, title: (t(item.label) ?? item.label ?? "") as string }}
            />
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0, pb: 0, px: 0 }}>
            <List sx={{ py: 0 }}>
              {item.children!.map((child) => {
                const childHasChildren = !!child.children?.length;
                const isChildSelected = activeSecondKey === child.key;

                return (
                  <ListItemButton
                    key={child.key}
                    selected={isChildSelected}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (childHasChildren) {
                        setActiveSecondKey(child.key);
                        setOpenDrawerKey(child.key);
                        setOpenThirdKey(null);
                        setActiveFirstLevel(item.key);
                        onSelectTopIcon?.(item.key);
                        return;
                      }

                      navigateSmart(child.path, child.state);

                      onRequestClose?.();
                    }}
                    sx={{
                      pl: 6,
                      borderRadius: 1,
                      mx: 1,
                      height: ROW_H,
                      py: 0,
                      alignItems: "center",
                      color: isChildSelected
                        ? getBrandCustom("primaryColor", "#0055A4")
                        : "#475467",
                      "&.Mui-selected": selectedPill,
                      ...(isChildSelected ? { "&:hover": selectedPill } : { "&:hover": hoverPill }),
                    }}
                  >
                    {child.icon && (
                      <ListItemIcon
                        sx={{ minWidth: 17, mr: 1.5, color: "inherit", alignItems: "center" }}
                      >
                        <SvgIconUrl src={child.icon} size={17} />
                      </ListItemIcon>
                    )}

                    <ListItemText
                      primary={t(child.label) ?? child.label ?? ""}
                      primaryTypographyProps={{ noWrap: true, title: (t(child.label) ?? child.label ?? "") as string }}
                    />

                    {childHasChildren && (
                      <Box
                        sx={{ ml: "auto", display: "flex", alignItems: "center", color: "inherit" }}
                      >
                        <SvgIconUrl src={NEXTICON} size={17} />
                      </Box>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <ListItemButton
        key={item.key}
        selected={isActive}
        onClick={(e) => {
          e.stopPropagation();

          navigateSmart(item.path, item.state);

          onRequestClose?.();
        }}
        sx={baseSx}
      >
        {item.icon && (
          <ListItemIcon sx={{ minWidth: 17, mr: 1.5, color: "inherit", alignItems: "center" }}>
            <SvgIconUrl src={item.icon} size={17} />
          </ListItemIcon>
        )}
        <ListItemText
          primary={t(item.label) ?? item.label ?? ""}
          primaryTypographyProps={{ noWrap: true, title: (t(item.label) ?? item.label ?? "") as string }}
        />
      </ListItemButton>
    );
  };

  return (
    <List ref={listRef} sx={{ py: 0 }}>
      {items.map(renderItem)}
    </List>
  );
};

export default FirstLevelList;
